import React, { useState, useEffect } from 'react';
import InfoTip from '../shared/InfoTip';
import { SearchBar, ExportMenu } from './shared';
import { WindowSelector } from './content-behavior/WindowSelector';
import ReferralsTab, { exportReferralsCsv, exportReferralsPdf } from './content-behavior/ReferralsTab';
import PagesTab, { exportPagesCsv, exportPagesPdf } from './content-behavior/PagesTab';
import GeoTab, { exportGeoCsv, exportGeoPdf } from './content-behavior/GeoTab';

export { classifyReferral } from './content-behavior/ReferralsTab';
export { resolvePageIntent } from './content-behavior/PagesTab';
export { TechTabContent, getTechSummary } from './content-behavior/TechTab';

const TABS = [
  { id: 'referrals', label: 'Referrals', tip: 'External sites linking to yours, with the exact referrer URLs and conversion counts. Categorized as Social, Search, AI, News, or Direct Link.' },
  { id: 'pages', label: 'Pages', tip: 'Per-page pageviews, bounce rate, engagement, and conversions. The Landing Pages tab shows pages where sessions started.' },
  { id: 'geo', label: 'Geo', tip: 'Sessions by country and state/region, with city-level drill-down on click. Use to confirm marketing reach matches your service area.' },
];

const ContentBehaviorTabs = ({
  referralsData, referralsLoading, referralsError, referralWindows, selectedReferralWindow, onReferralWindowChange,
  pagesData, pagesLoading, pagesError, pageWindows, selectedPageWindow, onPageWindowChange,
  landingPagesData, landingPagesLoading, landingPagesError,
  geoData, geoLoading, geoError, geoWindows, geoTrendData, geoTrendLoading, selectedGeoWindow, onGeoWindowChange,
  rangeStart, rangeEnd, selectedClientId,
}) => {
  const [activeTab, setActiveTab] = useState('referrals');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { setSearchQuery(''); }, [activeTab]);

  const tabExports = {
    referrals: {
      csv: () => exportReferralsCsv(referralsData, rangeStart, rangeEnd),
      pdf: () => exportReferralsPdf(referralsData, rangeStart, rangeEnd),
    },
    pages: {
      csv: () => exportPagesCsv(pagesData, landingPagesData, rangeStart, rangeEnd),
      pdf: () => exportPagesPdf(pagesData, landingPagesData, rangeStart, rangeEnd),
    },
    geo: {
      csv: () => exportGeoCsv(geoData, rangeStart, rangeEnd),
      pdf: () => exportGeoPdf(geoData, rangeStart, rangeEnd),
    },
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-5">
      {/* Tab bar */}
      <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-2 border-b dark:border-gray-700 mb-4">
        <div className="flex items-center gap-1 flex-wrap">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition -mb-px flex items-center whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-[#32B1FF] text-[#32B1FF]'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && tab.tip && <InfoTip text={tab.tip} />}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 pb-1 flex-wrap max-[589px]:w-full">
          <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder={`Search ${TABS.find(tab => tab.id === activeTab)?.label ?? ''}…`} />
          {activeTab === 'referrals' && (
            <><span className="text-xs text-gray-500 dark:text-gray-400">Window:</span><WindowSelector windows={referralWindows} selected={selectedReferralWindow} onChange={onReferralWindowChange} rangeStart={rangeStart} rangeEnd={rangeEnd} /></>
          )}
          {activeTab === 'pages' && (
            <><span className="text-xs text-gray-500 dark:text-gray-400">Window:</span><WindowSelector windows={pageWindows} selected={selectedPageWindow} onChange={onPageWindowChange} rangeStart={rangeStart} rangeEnd={rangeEnd} /></>
          )}
          {activeTab === 'geo' && (
            <><span className="text-xs text-gray-500 dark:text-gray-400">Window:</span><WindowSelector windows={geoWindows} selected={selectedGeoWindow} onChange={onGeoWindowChange} rangeStart={rangeStart} rangeEnd={rangeEnd} /></>
          )}
          <ExportMenu onCsv={tabExports[activeTab]?.csv} onPdf={tabExports[activeTab]?.pdf} />
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'referrals' && (
        <ReferralsTab
          referralsData={referralsData}
          referralsLoading={referralsLoading}
          referralsError={referralsError}
        />
      )}
      {activeTab === 'pages' && (
        <PagesTab
          pagesData={pagesData}
          pagesLoading={pagesLoading}
          pagesError={pagesError}
          landingPagesData={landingPagesData}
          landingPagesLoading={landingPagesLoading}
          landingPagesError={landingPagesError}
          searchQuery={searchQuery}
        />
      )}
      {activeTab === 'geo' && (
        <GeoTab
          geoData={geoData}
          geoLoading={geoLoading}
          geoError={geoError}
          geoWindows={geoWindows}
          geoTrendData={geoTrendData}
          geoTrendLoading={geoTrendLoading}
          selectedGeoWindow={selectedGeoWindow}
          searchQuery={searchQuery}
          selectedClientId={selectedClientId}
        />
      )}
    </div>
  );
};

export default ContentBehaviorTabs;
